// Local fixtures for Phase 4a UI-only. Later bridged to shared/DataService contracts in T3 wiring.

/* ------- Local types --------------------------------------------------- */

export interface MockUser {
  id: string;
  displayName: string;
  tenantId: string;
}

export interface Project {
  id: string;
  name: string;
  description: string;
  hasUnread: boolean;
}

/** @deprecated Groups now cover both "projects" and "communities". */
export type Community = Project;

/** Alias for the group concept. A group contains chats. */
export type Group = Project;

export interface Topic {
  id: string;
  /** Group id — `null` means the chat is standalone (no group). */
  projectId: string | null;
  title: string;
  messageCount: number;
  /** Epoch millis of last activity. */
  lastActivity: number;
  /** Epoch millis when the chat was pinned, or undefined if not pinned. */
  pinnedAt?: number;
}

/** Alias for the chat concept. A chat may belong to a group or be standalone. */
export type Chat = Topic;

export type MessageAuthorType = 'user' | 'agent';

export interface MessageAttachment {
  id: string;
  name: string;
  /** MIME type (may be empty for unknown) */
  mime: string;
  /** URL (absolute or relative) the DocViewer can open. */
  url: string;
  /**
   * Workspace-file identity, present only when this attachment REFERENCES a file in the artifacts
   * registry instead of carrying uploaded bytes. Its presence is the marker: an upload's `id` is a
   * storage key and an artifact's is a registry id, and both are plain strings, so nothing else in the
   * chip can tell the two apart. Carried as one unit so half an identity cannot exist — `revision` is
   * the version the message pinned, which is also what `url` addresses. Mock fixtures never set it.
   */
  artifact?: { id: string; revision?: number };
  /**
   * When this file's bytes were copied out of an external system, ISO-8601, on the attachments that came
   * from one. Its presence is what makes the chip say the file is a SNAPSHOT: a chat file is the state at
   * the moment it was sent and is never refreshed. Mock fixtures never set it.
   */
  importedAt?: string;
  /**
   * Where that copy came from, already vetted as a plain web url. Never presented as a way to open the
   * file — it opens only for whoever the external system lets in — and often absent, in which case the
   * chip still says when the snapshot was taken.
   */
  originWebUrl?: string;
  /**
   * The address of a file that lives in SharePoint and is only POINTED AT from this message — no bytes of
   * ours behind it. Its presence is what offers "Put it in the chat", which is the one action that turns
   * such a reference into a file of the conversation. Absent on every attachment the chat already holds.
   */
  sharePoint?: {
    connectionId?: string;
    siteId: string;
    driveId: string;
    itemId: string;
    webUrl?: string;
  };
  /** Live personal OneDrive identity. Its content URL always resolves the current source version. */
  oneDrive?: {
    scopeId: string;
    itemId: string;
    webUrl?: string;
  };
}

export interface Message {
  id: string;
  topicId: string;
  authorType: MessageAuthorType;
  authorUserId: string | null;
  authorAgentId: string | null;
  body: string;
  createdAt: number;
  attachments: MessageAttachment[];
  /**
   * Collaborative chat (global-workspaces phase 4): the display name of the user who sent this
   * message, resolved from the wire `sender_name` or the workspace member directory. Rendered as the
   * speaker label above another member's bubble in a shared thread. Null/absent on own messages,
   * assistant messages, and the mock fixtures.
   */
  senderName?: string | null;
  /** Optional parent pointer for chat-panel replies. */
  replyToMessageId?: string;
  /**
   * Origin marker: `'automation'` on synthetic user messages persisted by a
   * scheduler fire (`'automation_delivered'` when the firing's result went to
   * Teams, so the bubble is not rendered); `'secretary'` on an out-of-band
   * secretary post-back assistant message; `'automation_notice'` on the note a
   * run leaves about a file it could not reach. Mock fixtures never set it.
   */
  source?:
    | 'automation'
    | 'automation_delivered'
    | 'automation_notice'
    | 'quality_result'
    | 'skill_check'
    | 'dashboard_refresh'
    | 'secretary'
    | 'developer'
    | null;
  /**
   * Pairing anchor: on automation-cycle assistant messages — id of the
   * synthetic user message of their fire. Mock fixtures never set it.
   */
  anchorMessageId?: string | null;
  /** On a reply written by Retry: the question it answers again. */
  variantOf?: string | null;
  /** On a question: the reply variant its sender picked; null means the newest. */
  activeVariantId?: string | null;
  /**
   * Skill invocations batch-joined server-side — `source` is `'manual'` (a
   * composer chip) or `'auto'` (the agent's own auto-load). Absent/null for a
   * message with no invocation rows. Mock fixtures never set it.
   */
  skills?: Array<{ id: string; title: string; source: string }> | null;
}

export type SearchResultKind = 'project' | 'topic' | 'message';

export interface SearchResult {
  id: string;
  kind: SearchResultKind;
  title: string;
  subtitle: string;
  /** Refs to highlight in-app if ever wired. */
  projectId: string | null;
  topicId: string | null;
}

export interface Attachment {
  id: string;
  name: string;
  size: number;
  mime: string;
}

/* ------- Fixtures ------------------------------------------------------ */

export const mockUser: MockUser = {
  id: 'user-pavel',
  displayName: 'Pavel Antonau',
  tenantId: 'tenant-demo',
};

export const mockProjects: Project[] = [
  {
    id: 'proj-alpha',
    name: 'Alpha Launch',
    description: 'Go-to-market for v1 release.',
    hasUnread: true,
  },
  {
    id: 'proj-billing',
    name: 'Billing Rework',
    description: 'Plan migration + invoice polish.',
    hasUnread: false,
  },
  {
    id: 'proj-design',
    name: 'Design System',
    description: 'Component library refresh.',
    hasUnread: true,
  },
];

/** @deprecated Communities have been folded into groups + standalone chats. */
export const mockCommunities: Community[] = [];

/** Alias — code prefers `mockGroups` going forward. */
export const mockGroups: Group[] = mockProjects;

// Timestamps: use fixed-past offsets from a stable reference so tests are
// deterministic. `reference` is the "now" the UI would show when the fixture
// set was authored; relative-time helpers render against the real clock.
const reference = Date.UTC(2026, 3, 20, 12, 0, 0); // 2026-04-20T12:00Z

function hoursAgo(h: number): number {
  return reference - h * 60 * 60 * 1000;
}

function minutesAgo(m: number): number {
  return reference - m * 60 * 1000;
}

function daysAgo(d: number): number {
  return reference - d * 24 * 60 * 60 * 1000;
}

export const mockTopics: Record<string, Topic[]> = {
  'proj-alpha': [
    {
      id: 'topic-alpha-kickoff',
      projectId: 'proj-alpha',
      title: 'Kickoff notes',
      messageCount: 5,
      lastActivity: minutesAgo(12),
    },
    {
      id: 'topic-alpha-risk',
      projectId: 'proj-alpha',
      title: 'Risk register',
      messageCount: 3,
      lastActivity: hoursAgo(3),
    },
    {
      id: 'topic-alpha-q2',
      projectId: 'proj-alpha',
      title: 'Q2 targets',
      messageCount: 4,
      lastActivity: daysAgo(3),
    },
  ],
  'proj-billing': [
    {
      id: 'topic-billing-migration',
      projectId: 'proj-billing',
      title: 'Plan migration playbook',
      messageCount: 6,
      lastActivity: minutesAgo(45),
    },
    {
      id: 'topic-billing-invoices',
      projectId: 'proj-billing',
      title: 'Invoice polish',
      messageCount: 2,
      lastActivity: hoursAgo(8),
    },
  ],
  'proj-design': [
    {
      id: 'topic-design-tokens',
      projectId: 'proj-design',
      title: 'Colour tokens',
      messageCount: 4,
      lastActivity: minutesAgo(90),
    },
    {
      id: 'topic-design-buttons',
      projectId: 'proj-design',
      title: 'Button states',
      messageCount: 3,
      lastActivity: hoursAgo(5),
    },
    {
      id: 'topic-design-audit',
      projectId: 'proj-design',
      title: 'Accessibility audit',
      messageCount: 5,
      lastActivity: daysAgo(14),
    },
  ],
};

/**
 * Standalone chats — not attached to any group. Rendered under the "Chats"
 * section in Col A.
 */
export const mockStandaloneChats: Chat[] = [
  {
    id: 'chat-welcome',
    projectId: null,
    title: 'Welcome',
    messageCount: 4,
    lastActivity: minutesAgo(8),
  },
  {
    id: 'chat-daily-standup',
    projectId: null,
    title: 'Daily standup',
    messageCount: 3,
    lastActivity: hoursAgo(2),
  },
  {
    id: 'chat-roadmap',
    projectId: null,
    title: 'Roadmap',
    messageCount: 3,
    lastActivity: hoursAgo(6),
  },
  {
    id: 'chat-coffee',
    projectId: null,
    title: 'Coffee break',
    messageCount: 2,
    lastActivity: hoursAgo(1),
  },
  {
    // Yesterday bucket (24–48 h ago)
    id: 'chat-yesterday-sync',
    projectId: null,
    title: 'Sync notes',
    messageCount: 2,
    lastActivity: hoursAgo(30),
  },
  {
    // Previous 30 days bucket (8–30 days ago)
    id: 'chat-sprint-retro',
    projectId: null,
    title: 'Sprint retro',
    messageCount: 4,
    lastActivity: daysAgo(20),
  },
  {
    // Older bucket (31–60 days ago)
    id: 'chat-onboarding',
    projectId: null,
    title: 'Onboarding checklist',
    messageCount: 1,
    lastActivity: daysAgo(45),
  },
];

export const mockMessages: Record<string, Message[]> = {
  'topic-alpha-kickoff': [
    {
      id: 'msg-1',
      topicId: 'topic-alpha-kickoff',
      authorType: 'user',
      authorUserId: 'user-pavel',
      authorAgentId: null,
      body: 'Kickoff in 10 — can you pull the latest metrics?',
      createdAt: hoursAgo(2),
      attachments: [],
    },
    {
      id: 'msg-2',
      topicId: 'topic-alpha-kickoff',
      authorType: 'agent',
      authorUserId: null,
      authorAgentId: 'researcher',
      body: 'On it. Here is the current snapshot:\n\n```\nWAU  +4.2%\nMAU  +1.1%\nNPS  42 (+3)\n```',
      createdAt: hoursAgo(1),
      attachments: [
        {
          id: 'att-alpha-snapshot',
          name: 'alpha-snapshot.pdf',
          mime: 'application/pdf',
          url: '/sample.pdf',
        },
      ],
    },
    {
      id: 'msg-3',
      topicId: 'topic-alpha-kickoff',
      authorType: 'user',
      authorUserId: 'user-pavel',
      authorAgentId: null,
      body: 'Thanks. @researcher — can you highlight the two risks you flagged yesterday?',
      createdAt: minutesAgo(40),
      attachments: [],
    },
    {
      id: 'msg-4',
      topicId: 'topic-alpha-kickoff',
      authorType: 'agent',
      authorUserId: null,
      authorAgentId: 'researcher',
      body: 'Yes — onboarding drop-off at step 3 and email deliverability on Tuesday send.',
      createdAt: minutesAgo(25),
      attachments: [],
    },
    {
      id: 'msg-5',
      topicId: 'topic-alpha-kickoff',
      authorType: 'user',
      authorUserId: 'user-pavel',
      authorAgentId: null,
      body: 'Great — starting the call.',
      createdAt: minutesAgo(12),
      attachments: [],
    },
  ],
  'topic-alpha-risk': [
    {
      id: 'msg-r1',
      topicId: 'topic-alpha-risk',
      authorType: 'user',
      authorUserId: 'user-pavel',
      authorAgentId: null,
      body: 'Draft the register — top five risks, owner per row.',
      createdAt: hoursAgo(5),
      attachments: [],
    },
    {
      id: 'msg-r2',
      topicId: 'topic-alpha-risk',
      authorType: 'agent',
      authorUserId: null,
      authorAgentId: 'summariser',
      body: 'Drafted. Attached.',
      createdAt: hoursAgo(4),
      attachments: [
        {
          id: 'att-risk',
          name: 'risk-register.pdf',
          mime: 'application/pdf',
          url: '/sample.pdf',
        },
      ],
    },
    {
      id: 'msg-r3',
      topicId: 'topic-alpha-risk',
      authorType: 'user',
      authorUserId: 'user-pavel',
      authorAgentId: null,
      body: 'Send it to the PMO.',
      createdAt: hoursAgo(3),
      attachments: [],
    },
  ],
  'topic-alpha-q2': [
    {
      id: 'msg-q1',
      topicId: 'topic-alpha-q2',
      authorType: 'user',
      authorUserId: 'user-pavel',
      authorAgentId: null,
      body: 'What are we committing to in Q2?',
      createdAt: hoursAgo(24),
      attachments: [],
    },
    {
      id: 'msg-q2',
      topicId: 'topic-alpha-q2',
      authorType: 'agent',
      authorUserId: null,
      authorAgentId: 'router',
      body: 'Top three: activation uplift, onboarding v2, billing migration.',
      createdAt: hoursAgo(23),
      attachments: [],
    },
    {
      id: 'msg-q3',
      topicId: 'topic-alpha-q2',
      authorType: 'user',
      authorUserId: 'user-pavel',
      authorAgentId: null,
      body: 'Add the NPS target.',
      createdAt: hoursAgo(22),
      attachments: [],
    },
    {
      id: 'msg-q4',
      topicId: 'topic-alpha-q2',
      authorType: 'agent',
      authorUserId: null,
      authorAgentId: 'router',
      body: 'Added: NPS ≥ 45 by end of June.',
      createdAt: hoursAgo(20),
      attachments: [],
    },
  ],
  'topic-billing-migration': [
    {
      id: 'msg-bm1',
      topicId: 'topic-billing-migration',
      authorType: 'user',
      authorUserId: 'user-pavel',
      authorAgentId: null,
      body: 'Draft the migration playbook — phase by phase.',
      createdAt: hoursAgo(6),
      attachments: [],
    },
    {
      id: 'msg-bm2',
      topicId: 'topic-billing-migration',
      authorType: 'agent',
      authorUserId: null,
      authorAgentId: 'summariser',
      body: 'Phase 1: freeze legacy plans. Phase 2: dual-write new + old. Phase 3: swap.',
      createdAt: hoursAgo(5),
      attachments: [],
    },
    {
      id: 'msg-bm3',
      topicId: 'topic-billing-migration',
      authorType: 'user',
      authorUserId: 'user-pavel',
      authorAgentId: null,
      body: 'How long is the dual-write window?',
      createdAt: hoursAgo(2),
      attachments: [],
    },
    {
      id: 'msg-bm4',
      topicId: 'topic-billing-migration',
      authorType: 'agent',
      authorUserId: null,
      authorAgentId: 'summariser',
      body: '10 business days — enough to cover two billing cycles.',
      createdAt: hoursAgo(1),
      attachments: [],
    },
  ],
  'topic-billing-invoices': [
    {
      id: 'msg-bi1',
      topicId: 'topic-billing-invoices',
      authorType: 'user',
      authorUserId: 'user-pavel',
      authorAgentId: null,
      body: 'Invoice typography is noisy — we can tighten it.',
      createdAt: hoursAgo(10),
      attachments: [],
    },
    {
      id: 'msg-bi2',
      topicId: 'topic-billing-invoices',
      authorType: 'agent',
      authorUserId: null,
      authorAgentId: 'researcher',
      body: 'Drafted a cleaner layout and ran it through the legal-copy checklist.',
      createdAt: hoursAgo(8),
      attachments: [
        {
          id: 'att-invoice',
          name: 'invoice-mock.pdf',
          mime: 'application/pdf',
          url: '/sample.pdf',
        },
      ],
    },
  ],
  'topic-design-tokens': [
    {
      id: 'msg-dt1',
      topicId: 'topic-design-tokens',
      authorType: 'user',
      authorUserId: 'user-pavel',
      authorAgentId: null,
      body: 'We are moving to blue — drop the cream tokens.',
      createdAt: hoursAgo(3),
      attachments: [],
    },
    {
      id: 'msg-dt2',
      topicId: 'topic-design-tokens',
      authorType: 'agent',
      authorUserId: null,
      authorAgentId: 'router',
      body: 'Confirmed — replacing `--bg: #f4f2ee` with `--bg: #f5f8ff` across the system.',
      createdAt: hoursAgo(2),
      attachments: [],
    },
    {
      id: 'msg-dt3',
      topicId: 'topic-design-tokens',
      authorType: 'user',
      authorUserId: 'user-pavel',
      authorAgentId: null,
      body: 'Good. Keep the 8px rhythm.',
      createdAt: minutesAgo(120),
      attachments: [],
    },
    {
      id: 'msg-dt4',
      topicId: 'topic-design-tokens',
      authorType: 'agent',
      authorUserId: null,
      authorAgentId: 'router',
      body: 'Yes, 8/10/12/16/24 scale preserved.',
      createdAt: minutesAgo(90),
      attachments: [],
    },
  ],
  'topic-design-buttons': [
    {
      id: 'msg-db1',
      topicId: 'topic-design-buttons',
      authorType: 'user',
      authorUserId: 'user-pavel',
      authorAgentId: null,
      body: 'Primary button hover needs more contrast.',
      createdAt: hoursAgo(7),
      attachments: [],
    },
    {
      id: 'msg-db2',
      topicId: 'topic-design-buttons',
      authorType: 'agent',
      authorUserId: null,
      authorAgentId: 'researcher',
      body: 'Bumped `--accent-hover` to `#1f5ad1` — meets WCAG AA at 14px.',
      createdAt: hoursAgo(6),
      attachments: [],
    },
    {
      id: 'msg-db3',
      topicId: 'topic-design-buttons',
      authorType: 'user',
      authorUserId: 'user-pavel',
      authorAgentId: null,
      body: 'Ship it.',
      createdAt: hoursAgo(5),
      attachments: [],
    },
  ],
  'topic-design-audit': [
    {
      id: 'msg-da1',
      topicId: 'topic-design-audit',
      authorType: 'user',
      authorUserId: 'user-pavel',
      authorAgentId: null,
      body: 'Run an accessibility audit on the shell.',
      createdAt: hoursAgo(30),
      attachments: [],
    },
    {
      id: 'msg-da2',
      topicId: 'topic-design-audit',
      authorType: 'agent',
      authorUserId: null,
      authorAgentId: 'researcher',
      body: 'Four issues: focus rings, nav aria-label, mic button label, and contrast on the offline badge.',
      createdAt: hoursAgo(29),
      attachments: [],
    },
    {
      id: 'msg-da3',
      topicId: 'topic-design-audit',
      authorType: 'user',
      authorUserId: 'user-pavel',
      authorAgentId: null,
      body: 'Fix focus rings first.',
      createdAt: hoursAgo(28),
      attachments: [],
    },
    {
      id: 'msg-da4',
      topicId: 'topic-design-audit',
      authorType: 'agent',
      authorUserId: null,
      authorAgentId: 'router',
      body: 'Here is the proposed rule:\n\n```css\n:focus-visible { outline: 2px solid var(--accent); }\n```',
      createdAt: hoursAgo(27),
      attachments: [],
    },
    {
      id: 'msg-da5',
      topicId: 'topic-design-audit',
      authorType: 'user',
      authorUserId: 'user-pavel',
      authorAgentId: null,
      body: 'Ship it.',
      createdAt: hoursAgo(26),
      attachments: [],
    },
  ],
  'topic-comm-eng-welcome': [
    {
      id: 'msg-ew1',
      topicId: 'topic-comm-eng-welcome',
      authorType: 'user',
      authorUserId: 'user-maria',
      authorAgentId: null,
      body: '👋 Welcome to the Engineering community! This is where we share tips, post cool PRs, and ask questions.',
      createdAt: hoursAgo(48),
      attachments: [],
    },
    {
      id: 'msg-ew2',
      topicId: 'topic-comm-eng-welcome',
      authorType: 'user',
      authorUserId: 'user-jin',
      authorAgentId: null,
      body: 'Hey folks — I just landed the streaming sync diff. Happy to walk anyone through it over coffee.',
      createdAt: hoursAgo(6),
      attachments: [],
    },
    {
      id: 'msg-ew3',
      topicId: 'topic-comm-eng-welcome',
      authorType: 'user',
      authorUserId: 'user-aisha',
      authorAgentId: null,
      body: 'Quick reminder: please tag @router when you need a cross-team review. Saves us a day.',
      createdAt: hoursAgo(2),
      attachments: [],
    },
    {
      id: 'msg-ew4',
      topicId: 'topic-comm-eng-welcome',
      authorType: 'user',
      authorUserId: 'user-pavel',
      authorAgentId: null,
      body: 'New UI shell is on staging — take it for a spin and drop feedback here.',
      createdAt: minutesAgo(8),
      attachments: [],
    },
  ],
  'topic-comm-eng-standup': [
    {
      id: 'msg-es1',
      topicId: 'topic-comm-eng-standup',
      authorType: 'user',
      authorUserId: 'user-jin',
      authorAgentId: null,
      body: 'Yesterday: finished the SSE retry path. Today: two-user e2e. Blockers: none.',
      createdAt: hoursAgo(3),
      attachments: [],
    },
    {
      id: 'msg-es2',
      topicId: 'topic-comm-eng-standup',
      authorType: 'user',
      authorUserId: 'user-maria',
      authorAgentId: null,
      body: 'Y: landed tenant-guard tests. T: adapter conformance. Blockers: none.',
      createdAt: hoursAgo(3),
      attachments: [],
    },
    {
      id: 'msg-es3',
      topicId: 'topic-comm-eng-standup',
      authorType: 'user',
      authorUserId: 'user-aisha',
      authorAgentId: null,
      body: 'Y: reviewed PRs. T: migration runner edge cases. Need a pair on race between drain + SSE — ping me.',
      createdAt: hoursAgo(2),
      attachments: [],
    },
  ],
  'topic-comm-product-welcome': [
    {
      id: 'msg-pw1',
      topicId: 'topic-comm-product-welcome',
      authorType: 'user',
      authorUserId: 'user-dani',
      authorAgentId: null,
      body: '👋 Welcome to Product Council! We meet Thursdays, but most decisions happen right here.',
      createdAt: hoursAgo(72),
      attachments: [],
    },
    {
      id: 'msg-pw2',
      topicId: 'topic-comm-product-welcome',
      authorType: 'user',
      authorUserId: 'user-sam',
      authorAgentId: null,
      body: 'Posting the Q2 priority doc in # roadmap — please read before next council.',
      createdAt: hoursAgo(10),
      attachments: [],
    },
    {
      id: 'msg-pw3',
      topicId: 'topic-comm-product-welcome',
      authorType: 'user',
      authorUserId: 'user-maria',
      authorAgentId: null,
      body: 'Agreed on the activation wedge — ticket is up.',
      createdAt: hoursAgo(4),
      attachments: [],
    },
    {
      id: 'msg-pw4',
      topicId: 'topic-comm-product-welcome',
      authorType: 'user',
      authorUserId: 'user-pavel',
      authorAgentId: null,
      body: 'Thanks all. Keep it flowing — async works.',
      createdAt: minutesAgo(30),
      attachments: [],
    },
  ],
  'topic-comm-product-roadmap': [
    {
      id: 'msg-pr1',
      topicId: 'topic-comm-product-roadmap',
      authorType: 'user',
      authorUserId: 'user-sam',
      authorAgentId: null,
      body: 'Draft roadmap attached — please comment by EoW.',
      createdAt: hoursAgo(8),
      attachments: [
        {
          id: 'att-roadmap',
          name: 'Q2-roadmap.pdf',
          mime: 'application/pdf',
          url: '/sample.pdf',
        },
      ],
    },
    {
      id: 'msg-pr2',
      topicId: 'topic-comm-product-roadmap',
      authorType: 'user',
      authorUserId: 'user-dani',
      authorAgentId: null,
      body: 'Nit: move the billing migration ahead of tokens — unblocks finance.',
      createdAt: hoursAgo(7),
      attachments: [],
    },
    {
      id: 'msg-pr3',
      topicId: 'topic-comm-product-roadmap',
      authorType: 'user',
      authorUserId: 'user-pavel',
      authorAgentId: null,
      body: 'Good call. Swapping the order.',
      createdAt: hoursAgo(6),
      attachments: [],
    },
  ],
};

export const mockSearchResults: SearchResult[] = [
  {
    id: 'sr-1',
    kind: 'project',
    title: 'Alpha Launch',
    subtitle: 'Go-to-market for v1 release.',
    projectId: 'proj-alpha',
    topicId: null,
  },
  {
    id: 'sr-2',
    kind: 'project',
    title: 'Billing Rework',
    subtitle: 'Plan migration + invoice polish.',
    projectId: 'proj-billing',
    topicId: null,
  },
  {
    id: 'sr-3',
    kind: 'project',
    title: 'Design System',
    subtitle: 'Component library refresh.',
    projectId: 'proj-design',
    topicId: null,
  },
  {
    id: 'sr-4',
    kind: 'topic',
    title: 'Kickoff notes',
    subtitle: 'Alpha Launch',
    projectId: 'proj-alpha',
    topicId: 'topic-alpha-kickoff',
  },
  {
    id: 'sr-5',
    kind: 'topic',
    title: 'Risk register',
    subtitle: 'Alpha Launch',
    projectId: 'proj-alpha',
    topicId: 'topic-alpha-risk',
  },
  {
    id: 'sr-6',
    kind: 'topic',
    title: 'Colour tokens',
    subtitle: 'Design System',
    projectId: 'proj-design',
    topicId: 'topic-design-tokens',
  },
  {
    id: 'sr-7',
    kind: 'message',
    title:
      'Thanks. @researcher — can you highlight the two risks you flagged yesterday?',
    subtitle: 'Alpha Launch · Kickoff notes',
    projectId: 'proj-alpha',
    topicId: 'topic-alpha-kickoff',
  },
  {
    id: 'sr-8',
    kind: 'message',
    title: 'Bumped `--accent-hover` to `#1f5ad1` — meets WCAG AA at 14px.',
    subtitle: 'Design System · Button states',
    projectId: 'proj-design',
    topicId: 'topic-design-buttons',
  },
];

export const mockAttachments: Attachment[] = [];

/* ------- Helpers ------------------------------------------------------- */

export function getTopicsForProject(projectId: string | null): Topic[] {
  if (projectId === null) {
    return [];
  }
  return mockTopics[projectId] ?? [];
}

/** Alias — chats-for-group in new terminology. */
export const getChatsForGroup = getTopicsForProject;

/** Flat list of chats not attached to any group. */
export function getStandaloneChats(): Chat[] {
  return mockStandaloneChats;
}

export function getMessagesForTopic(topicId: string | null): Message[] {
  if (topicId === null) {
    return [];
  }
  return mockMessages[topicId] ?? [];
}

export function findProjectName(projectId: string | null): string | null {
  if (projectId === null) {
    return null;
  }
  const proj = mockProjects.find((p) => p.id === projectId);
  if (proj !== undefined) {
    return proj.name;
  }
  const comm = mockCommunities.find((c) => c.id === projectId);
  if (comm !== undefined) {
    return comm.name;
  }
  return null;
}

export function findTopicTitle(topicId: string | null): string | null {
  if (topicId === null) {
    return null;
  }
  for (const list of Object.values(mockTopics)) {
    const t = list.find((t) => t.id === topicId);
    if (t !== undefined) {
      return t.title;
    }
  }
  const standalone = mockStandaloneChats.find((c) => c.id === topicId);
  if (standalone !== undefined) {
    return standalone.title;
  }
  return null;
}

/** First attachment across all messages in a topic — used by the doc button. */
export function firstAttachmentInTopic(
  topicId: string | null,
): MessageAttachment | null {
  if (topicId === null) {
    return null;
  }
  const msgs = mockMessages[topicId] ?? [];
  for (const m of msgs) {
    if (m.attachments.length > 0) {
      return m.attachments[0] ?? null;
    }
  }
  return null;
}

/** Every attachment in a single topic, annotated with its source message id
 * so the UI can offer a "jump to chat" action per file. */
export interface AttachmentRef {
  attachment: MessageAttachment;
  messageId: string;
  topicId: string;
  topicTitle: string | null;
}

export function attachmentsForTopic(topicId: string | null): AttachmentRef[] {
  if (topicId === null) return [];
  const topicTitle = findTopicTitle(topicId);
  const msgs = mockMessages[topicId] ?? [];
  const out: AttachmentRef[] = [];
  for (const m of msgs) {
    for (const att of m.attachments) {
      out.push({ attachment: att, messageId: m.id, topicId, topicTitle });
    }
  }
  return out;
}

/** Every attachment across every topic, for the "All files" catalogue. */
export function allAttachments(): AttachmentRef[] {
  const out: AttachmentRef[] = [];
  for (const [topicId, msgs] of Object.entries(mockMessages)) {
    const topicTitle = findTopicTitle(topicId);
    for (const m of msgs) {
      for (const att of m.attachments) {
        out.push({ attachment: att, messageId: m.id, topicId, topicTitle });
      }
    }
  }
  return out;
}

/** Replies to a parent message, resolved across the same topic. Used by
 * the chat panel. */
export function getRepliesFor(parentMessageId: string): Message[] {
  for (const list of Object.values(mockMessages)) {
    const parent = list.find((m) => m.id === parentMessageId);
    if (parent === undefined) {
      continue;
    }
    return list.filter((m) => m.replyToMessageId === parentMessageId);
  }
  return [];
}

/**
 * Create a new standalone chat (no group) and prepend it to
 * `mockStandaloneChats` so it appears at the top of the flat list.
 * Returns the new `Chat` so callers can select it immediately.
 */
export function createNewChat(title = 'New Chat'): Chat {
  const id = `chat-new-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  const newChat: Chat = {
    id,
    projectId: null,
    title,
    messageCount: 0,
    lastActivity: Date.now(),
  };
  mockStandaloneChats.unshift(newChat);
  return newChat;
}

/** Locate a message by id across all topics. */
export function findMessageById(id: string | null): Message | null {
  if (id === null) {
    return null;
  }
  for (const list of Object.values(mockMessages)) {
    const hit = list.find((m) => m.id === id);
    if (hit !== undefined) {
      return hit;
    }
  }
  return null;
}

/**
 * Append a reply message under a parent — used by the chat panel's
 * miniature composer. The parent's topic is reused so the reply appears
 * in the main pane as well as the chat panel.
 */
export function appendReply(args: {
  parentMessageId: string;
  body: string;
  authorUserId: string;
}): Message | null {
  for (const [topicId, list] of Object.entries(mockMessages)) {
    const parent = list.find((m) => m.id === args.parentMessageId);
    if (parent === undefined) {
      continue;
    }
    const id = `msg-reply-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const message: Message = {
      id,
      topicId,
      authorType: 'user',
      authorUserId: args.authorUserId,
      authorAgentId: null,
      body: args.body,
      createdAt: Date.now(),
      attachments: [],
      replyToMessageId: args.parentMessageId,
    };
    list.push(message);
    return message;
  }
  return null;
}
